import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, Sparkles, CheckCircle, XCircle } from 'lucide-react';

// Global lock at module level - persists across component remounts
let isProcessingOAuth = false;
let processedCodes = new Set();

export default function AuthCallback({ onProcessed }) {
  const [searchParams] = useSearchParams();
  const { processGoogleCallback } = useAuth();
  const [status, setStatus] = useState('processing');

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');
    
    // Check if already processing or code already processed
    if (isProcessingOAuth) {
      console.log('OAuth already in progress, skipping');
      return;
    }
    
    if (code && processedCodes.has(code)) {
      console.log('OAuth code already processed, skipping');
      setStatus('success');
      onProcessed?.();
      return;
    }

    const processAuth = async () => {
      try {
        if (error) {
          console.error('Google OAuth error:', error);
          setStatus('error');
          onProcessed?.();
          setTimeout(() => {
            window.location.href = '/';
          }, 1500);
          return;
        }

        if (!code) {
          console.error('No authorization code found in URL');
          setStatus('error');
          onProcessed?.();
          setTimeout(() => {
            window.location.href = '/';
          }, 1500);
          return;
        }

        // Set global lock BEFORE any async operation
        isProcessingOAuth = true;
        processedCodes.add(code);
        
        // Process the Google OAuth callback
        const user = await processGoogleCallback(code, state);
        setStatus('success');
        
        // Mark as processed
        onProcessed?.();
        
        // Navigate to appropriate page
        setTimeout(() => {
          isProcessingOAuth = false;
          const targetPath = user.onboarding_completed ? '/dashboard' : '/onboarding';
          window.location.href = targetPath;
        }, 500);
        
      } catch (error) {
        console.error('Auth callback error:', error);
        isProcessingOAuth = false;
        setStatus('error');
        onProcessed?.();
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
      }
    };

    processAuth();
  }, [searchParams, processGoogleCallback, onProcessed]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
        {status === 'success' ? (
          <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-4" />
        ) : status === 'error' ? (
          <XCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
        ) : (
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-4" />
        )}
        <p className="text-muted-foreground">
          {status === 'success' ? 'Connexion réussie ! Redirection...' : 
           status === 'error' ? 'Erreur de connexion...' : 'Connexion en cours...'}
        </p>
      </div>
    </div>
  );
}
